import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import CurrencyFormat from 'react-currency-format';
import { CompetitorPriceDTO, CompetitorsInfo } from '../../../../../../../@types';
import { Competitors, PhotoModal } from '../../../../../../../components';
import { LoadingSpinerArea } from '../../../../../../../components/LoadingSpinner';
import { parseStringToPlural } from '../../../../../../../utils';
import { getCampaignCompetitorsInfo } from '../../services';
import { IRowData } from './OfferTable';

const originPrice = {
  'INFOPRICE - INFOPANEL': 'INFOPANEL',
  'INFOPRICE - MPDV': 'MPDV',
} as const;

interface OfferCompetitorsExpandedRowProps {
  rowData: IRowData;
  campaignId: string;
  storeIds: string[];
}

export const OfferCompetitorsExpandedRow = ({ rowData, campaignId, storeIds }: OfferCompetitorsExpandedRowProps) => {
  const productId = rowData.product_id;
  const normalizedStoreIds = useMemo(() => Array.from(new Set((storeIds ?? []).filter(Boolean))), [storeIds]);
  const loadingArea = `competitor-row-${campaignId}-${productId}`;

  const [photoModal, setPhotoModal] = useState<CompetitorPriceDTO['photo']>(null);

  const { data } = useQuery({
    queryKey: ['ipa/campanhas/ofertas/competitors-info', campaignId, productId, normalizedStoreIds],
    enabled: Boolean(campaignId && productId && normalizedStoreIds.length),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await getCampaignCompetitorsInfo({ campaignId, productId, storeIds: normalizedStoreIds }, loadingArea);
      return res as CompetitorsInfo;
    },
  });

  const resumeCardData = data?.competitor ?? data?.competitorInfoPanel;
  const total = resumeCardData?.countCompetitorStore ?? 0;
  const hasCompetitorsDto = Array.isArray(data?.competitorPriceDTO) && data.competitorPriceDTO.length > 0;

  return (
    <>
      <LoadingSpinerArea area={loadingArea} height="100%" size="md" />
      <Competitors>
        {!resumeCardData && <p className="competitor-empty">Informações de concorrência indisponíveis</p>}

        {!!resumeCardData && (
          <>
            <Competitors.Section>
              <Competitors.Header>
                <Competitors.Icon icon="MdDescInsights" />
                <Competitors.Title>Concorrentes</Competitors.Title>
              </Competitors.Header>

              <Competitors.Content>
                <Competitors.Card>
                  <Competitors.CardHeader>
                    <Competitors.Title>{data?.competitor ? 'Concorrência direta' : 'Concorrência Infopanel'}</Competitors.Title>
                    <Competitors.Subtitle>
                      Produto encontrado em{' '}
                      <strong>
                        {total} {parseStringToPlural('loja', total)}
                      </strong>
                    </Competitors.Subtitle>
                  </Competitors.CardHeader>

                  <Competitors.CardContent>
                    <Competitors.InfoBox>
                      <Competitors.Value>
                        <CurrencyFormat
                          fixedDecimalScale
                          decimalScale={2}
                          value={resumeCardData.competitorAverage}
                          displayType="text"
                          prefix=" R$"
                          decimalSeparator=","
                          thousandSeparator="."
                        />
                      </Competitors.Value>
                      <Competitors.Label>
                        x̄ Preço
                        <br />
                        Médio
                      </Competitors.Label>
                    </Competitors.InfoBox>

                    <Competitors.InfoBox>
                      <Competitors.Value>
                        <CurrencyFormat
                          fixedDecimalScale
                          decimalScale={2}
                          value={resumeCardData.competitorLower}
                          displayType="text"
                          prefix=" R$"
                          decimalSeparator=","
                          thousandSeparator="."
                        />
                      </Competitors.Value>
                      <Competitors.Label>
                        {'<'} Preço
                        <br />
                        Mais Baixo
                      </Competitors.Label>
                    </Competitors.InfoBox>

                    <Competitors.InfoBox>
                      <Competitors.Value>
                        <CurrencyFormat
                          fixedDecimalScale
                          decimalScale={2}
                          value={resumeCardData.competitorHigher}
                          displayType="text"
                          prefix=" R$"
                          decimalSeparator=","
                          thousandSeparator="."
                        />
                      </Competitors.Value>
                      <Competitors.Label>
                        {'>'} Preço
                        <br />
                        Mais Alto
                      </Competitors.Label>
                    </Competitors.InfoBox>
                  </Competitors.CardContent>
                </Competitors.Card>
              </Competitors.Content>
            </Competitors.Section>
            {hasCompetitorsDto && (
              <>
                <Competitors.Separator />
                <Competitors.Section>
                  <Competitors.Header>
                    <Competitors.Icon icon="DescArrowForward" />
                    <Competitors.Title>Lojas</Competitors.Title>
                  </Competitors.Header>

                  <Competitors.Content>
                    {data?.competitorPriceDTO?.map(competitor => (
                      <Competitors.Card key={`${competitor.competitorStoreId}-${competitor.competitorStore}`}>
                        <Competitors.CardHeader>
                          <Competitors.Title>{competitor.competitorStore}</Competitors.Title>
                          <Competitors.CardHeaderAction>
                            <Competitors.Tag>{originPrice[competitor.origemPreco]}</Competitors.Tag>
                            <Competitors.Label>{competitor.competitorStoreType}</Competitors.Label>
                            {competitor.photo && (
                              <Competitors.PhotoIcon
                                onClick={() => {
                                  setPhotoModal(competitor.photo ?? null);
                                }}
                              />
                            )}
                          </Competitors.CardHeaderAction>
                        </Competitors.CardHeader>

                        <Competitors.CardContent>
                          <Competitors.InfoBox>
                            <Competitors.Label>Preço</Competitors.Label>
                            <Competitors.Value>
                              <CurrencyFormat
                                fixedDecimalScale
                                decimalScale={2}
                                value={competitor?.retailPrice ?? 0}
                                displayType="text"
                                prefix="R$ "
                                decimalSeparator=","
                                thousandSeparator="."
                              />
                            </Competitors.Value>
                          </Competitors.InfoBox>

                          <Competitors.InfoBox>
                            <Competitors.Label>Competitividade</Competitors.Label>
                            <Competitors.Value>
                              <CurrencyFormat
                                fixedDecimalScale
                                decimalScale={2}
                                value={rowData.retail_price && competitor.retailPrice ? (rowData.retail_price / competitor.retailPrice) * 100 : 0}
                                displayType="text"
                                suffix="%"
                                decimalSeparator=","
                                thousandSeparator="."
                              />
                            </Competitors.Value>
                          </Competitors.InfoBox>

                          <Competitors.InfoBox>
                            <Competitors.Label>Data</Competitors.Label>
                            <Competitors.Value>{competitor.date ? format(parseISO(competitor.date), 'dd/MM/yyyy') : '--'}</Competitors.Value>
                          </Competitors.InfoBox>
                        </Competitors.CardContent>
                      </Competitors.Card>
                    ))}
                  </Competitors.Content>
                </Competitors.Section>
              </>
            )}
          </>
        )}
      </Competitors>

      {photoModal && <PhotoModal show={true} onHide={() => setPhotoModal(null)} photo={photoModal} />}
    </>
  );
};
